import {
  clone,
  create,
  equals,
  fromJson,
  type JsonValue,
  type MessageInitShape,
  toJson,
} from "@bufbuild/protobuf";
import { v4 as uuidv4 } from "uuid";

import type { MetricChartPlotStyle } from "@/components/charts/chart.types";
import type { TableColumnLayout } from "@/components/data-display/table-layout";

import {
  ChartSchema,
  type MetricConfig,
  MetricConfigSchema,
} from "@/gen_src/proto/chalk/artifacts/v1/chart_pb";
import {
  type Dashboard,
  type DashboardStatisticViz,
  type DashboardWidget,
  DashboardWidgetSchema,
  type DashboardSchema,
  type DashboardDataWidget,
  DashboardDataWidgetSchema,
  type DashboardConnectionHealthWidgetSchema,
  DashboardTableVizSchema,
  type DashboardLatestDeploymentWidgetSchema,
  type DashboardMarkdownWidgetSchema,
  type DashboardNotebookCellWidgetSchema,
  type DashboardSectionTitleWidgetSchema,
  type GridPositionSchema,
  DashboardMonitorWidgetSchema,
  DashboardIncidentWidgetSchema,
} from "@/gen_src/proto/chalk/artifacts/v1/dashboard_pb";
import {
  type ContentSharingSchema,
  ContentRole,
  ContentVisibility,
} from "@/gen_src/proto/chalk/artifacts/v1/contentgrant_pb";
import {
  type CreateDashboardRequestSchema,
  DashboardControlsSchema,
  type GetDashboardResponse,
  GetDashboardResponseSchema,
  type UpdateDashboardRequestSchema,
} from "@/gen_src/proto/chalk/server/v1/dashboard_service_pb";
import { type ChartWithDefinedConfig } from "@/utils/metrics/metrics-types";
import {
  applyLayout,
  compactVertical,
  findFreeSpotInBand,
  GRID_COLS,
  layoutsEqual,
  type WidgetPlacement,
  type WidgetLayout,
} from "@/components/dashboards/layout/grid.utils";
import {
  isAggregationView,
  legacyCountByToState,
  measureLabel,
} from "@/components/aggregations/aggregationBuilder";
import {
  AGGREGATION_FN_LABEL,
  type AggregateVisualization,
  type AggregationBuilderState,
  type AggregationFacetOption,
} from "@/components/aggregations/aggregationBuilder.constants";
import { aggregateOptions } from "@/components/aggregations/aggregationRequest";
import { widgetSourceOf, type WidgetSourceSpec } from "@/components/dashboards/widgetSources";
import { parseCountByQuery } from "@/components/faceted-search/facetedSearchCountBy";
import { AggregateOptionsSchema } from "@/gen_src/proto/chalk/searchaggregates/v1/aggregation_pb";
import { MonitorKind } from "@/components/monitors/monitor-utils";
import { DEFAULT_DASHBOARD_CONNECTION_HEALTH } from "@/components/overview/overview-connections";

/**
 * Mapping between the wire `Dashboard` and the shapes the UI works with, kept out of the hooks so the
 * request/update-mask rules stay pure and unit-tested. Builders return protobuf-es init shapes.
 */

export type GridPositionInit = MessageInitShape<typeof GridPositionSchema>;
export type DashboardDataWidgetInit = MessageInitShape<typeof DashboardDataWidgetSchema>;
export type DashboardConnectionHealthWidgetInit = MessageInitShape<
  typeof DashboardConnectionHealthWidgetSchema
>;
export type DashboardLatestDeploymentWidgetInit = MessageInitShape<
  typeof DashboardLatestDeploymentWidgetSchema
>;
export type DashboardMonitorWidgetInit = {
  monitorId: string;
  monitorType: MonitorKind;
};
export type DashboardIncidentWidgetInit = MessageInitShape<typeof DashboardIncidentWidgetSchema>;
export type DashboardMarkdownWidgetInit = MessageInitShape<typeof DashboardMarkdownWidgetSchema>;
export type DashboardNotebookCellWidgetInit = MessageInitShape<
  typeof DashboardNotebookCellWidgetSchema
>;
export type DashboardSectionTitleWidgetInit = MessageInitShape<
  typeof DashboardSectionTitleWidgetSchema
>;
export type DashboardWidgetInit = MessageInitShape<typeof DashboardWidgetSchema>;

/** Returns whether two ordered dashboard layouts have the same persisted widget content. */
export const dashboardWidgetListsEqual = (
  left: readonly DashboardWidget[],
  right: readonly DashboardWidgetInit[],
): boolean =>
  left.length === right.length &&
  left.every((widget, index) =>
    equals(DashboardWidgetSchema, widget, create(DashboardWidgetSchema, right[index])),
  );

/** The `definition` oneof in init form — which kind of widget this is plus its payload. */
export type DashboardWidgetDefinitionInit = DashboardWidgetInit["definition"];
export type DashboardControlsInit = MessageInitShape<typeof DashboardControlsSchema>;
export type ContentSharingInit = MessageInitShape<typeof ContentSharingSchema>;
type DashboardInit = MessageInitShape<typeof DashboardSchema>;
type CreateDashboardRequestInit = MessageInitShape<typeof CreateDashboardRequestSchema>;
export type UpdateDashboardRequestInit = MessageInitShape<typeof UpdateDashboardRequestSchema>;

/** The data widget's data source, i.e. the set `data_query` oneof case. */
export type DataWidgetQueryCase = NonNullable<DashboardDataWidget["dataQuery"]["case"]>;
/** The data widget's rendering, i.e. the set `visualization` oneof case. */
export type DataWidgetVizCase = NonNullable<DashboardDataWidget["visualization"]["case"]>;

/**
 * The source×visualization compatibility matrix, mirroring the server's validateDataWidget. A
 * sourceQuery's options depend on its data_source ("logs" is the only one today).
 */
export const COMPATIBLE_VISUALIZATIONS: Record<DataWidgetQueryCase, readonly DataWidgetVizCase[]> =
  {
    metricQuery: ["timeseries", "statistic"],
    sourceQuery: ["timeseries", "table", "treeMap", "pie", "statistic"],
  };

/** Which part-to-whole view a case draws, or null for a case that draws something else. Takes the
 *  oneof's own type, which is unset on a widget that named no rendering. */
export const vizCasePartToWhole = (
  viz: DataWidgetVizCase | undefined,
): AggregateVisualization | null => (viz === "treeMap" ? "treemap" : viz === "pie" ? "pie" : null);

/** The faceted-search surface a source-query widget reads, or null for anything else. */
export const dataWidgetSource = (dataWidget: DashboardDataWidget): WidgetSourceSpec | null =>
  dataWidget.dataQuery.case === "sourceQuery"
    ? widgetSourceOf(dataWidget.dataQuery.value.dataSource)
    : null;

/** The widget's query string, or null if it isn't a source query this build knows how to render. */
export const dataWidgetSourceQuery = (dataWidget: DashboardDataWidget): string | null =>
  dataWidget.dataQuery.case === "sourceQuery" && dataWidgetSource(dataWidget) != null
    ? dataWidget.dataQuery.value.query
    : null;

/**
 * The tile's aggregation as proto options, or null for the raw list. Widgets saved before
 * `aggregate_options` carry a trailing `COUNT BY <facet> [LIMIT n]` inside the query instead, so that
 * clause is read as the COUNT it always meant; the server rejects a widget holding both.
 */
export const dataWidgetAggregateOptions = (
  dataWidget: DashboardDataWidget,
): MessageInitShape<typeof AggregateOptionsSchema> | null => {
  if (dataWidget.dataQuery.case !== "sourceQuery" || dataWidgetSource(dataWidget) == null) {
    return null;
  }
  const stored = dataWidget.dataQuery.value.aggregateOptions;
  if (stored != null) return stored;
  const { clause } = parseCountByQuery(dataWidget.dataQuery.value.query);
  return clause == null ? null : aggregateOptions(legacyCountByToState(clause));
};

/**
 * Read-mask paths for a listing that never draws a board — the server skips the per-widget decode
 * when `widgets` is out of the mask. Paths are proto (snake_case) names; camelCase matches nothing.
 */
export const DASHBOARD_LIST_READ_MASK_PATHS: string[] = [
  "id",
  "name",
  "description",
  "created_at",
  "updated_at",
  "created_by",
  "total_view_count",
  "viewer_last_viewed_at",
  "visibility",
  "viewer_role",
];

/** Just enough to resolve a dashboard id to its display name. */
export const DASHBOARD_NAME_READ_MASK_PATHS: string[] = ["id", "name"];

/**
 * A new, stable id for a dashboard widget. The proto requires the widget id to be
 * client-generated and unique within a dashboard; the server does not assign it.
 */
export const newWidgetId = (): string => uuidv4();

/** Build a widget from its definition, generating an id when one isn't supplied (the create case). */
const makeWidget = (
  definition: DashboardWidgetDefinitionInit,
  position: GridPositionInit,
  id?: string,
): DashboardWidgetInit => ({
  id: id ?? newWidgetId(),
  position,
  definition,
});

export const makeDataWidget = (args: {
  dataWidget: DashboardDataWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "dataWidget", value: args.dataWidget }, args.position, args.id);

export const makeMonitorWidget = (args: {
  monitorWidget: DashboardMonitorWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "monitorWidget", value: args.monitorWidget }, args.position, args.id);

export const makeConnectionHealthWidget = (args: {
  connectionHealthWidget: DashboardConnectionHealthWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget(
    { case: "connectionHealth", value: args.connectionHealthWidget },
    args.position,
    args.id,
  );

export const makeIncidentWidget = (args: {
  incidentWidget: DashboardIncidentWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "incidentWidget", value: args.incidentWidget }, args.position, args.id);

export const makeLatestDeploymentWidget = (args: {
  latestDeployment: DashboardLatestDeploymentWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "latestDeployment", value: args.latestDeployment }, args.position, args.id);

export const makeMarkdownWidget = (args: {
  markdown: DashboardMarkdownWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "markdown", value: args.markdown }, args.position, args.id);

export const makeSectionTitleWidget = (args: {
  sectionTitle: DashboardSectionTitleWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "sectionTitle", value: args.sectionTitle }, args.position, args.id);

export const makeNotebookCellWidget = (args: {
  notebookCell: DashboardNotebookCellWidgetInit;
  position: GridPositionInit;
  id?: string;
}): DashboardWidgetInit =>
  makeWidget({ case: "notebookCell", value: args.notebookCell }, args.position, args.id);

export const buildCreateDashboardRequest = (args: {
  name: string;
  description?: string;
  widgets?: DashboardWidgetInit[];
  controls?: DashboardControlsInit;
  sharing?: ContentSharingInit;
}): CreateDashboardRequestInit => ({
  // Only `name`, `description`, and `widgets` are read off the artifact on create; the rest is
  // server-populated.
  dashboard: {
    name: args.name,
    widgets: args.widgets ?? [],
    ...(args.description !== undefined ? { description: args.description } : {}),
  },
  ...(args.controls !== undefined ? { controls: args.controls } : {}),
  ...(args.sharing !== undefined ? { sharing: args.sharing } : {}),
});

/** Editing covers rename, widget edits, controls, and delete; the server gates all of them alike. */
export const canEditDashboard = (role: ContentRole | undefined): boolean =>
  role === ContentRole.EDITOR || role === ContentRole.OWNER;

/** Only the creator changes who a dashboard is shared with. */
export const canShareDashboard = (role: ContentRole | undefined): boolean =>
  role === ContentRole.OWNER;

export const isRestrictedDashboard = (visibility: ContentVisibility | undefined): boolean =>
  visibility === ContentVisibility.RESTRICTED;

/**
 * Build an update request, deriving `update_mask` from the fields supplied. The mask covers
 * `name`/`description`/`widgets` (widgets is a full replace); `controls` is separate and
 * replace-if-present.
 *
 * `description` is nullable server-side: pass `null` to clear it (the path is masked with the field
 * left unset), a string to set it, and omit it to leave the stored description alone.
 */
export const buildUpdateDashboardRequest = (args: {
  id: string;
  name?: string;
  description?: string | null;
  widgets?: DashboardWidgetInit[];
  controls?: DashboardControlsInit;
}): UpdateDashboardRequestInit => {
  const paths: string[] = [];
  const dashboard: DashboardInit = {
    id: args.id,
    ...(args.name !== undefined ? { name: args.name } : {}),
    // null masks the path while leaving the field unset, which is how the server is told to clear it.
    ...(args.description != null ? { description: args.description } : {}),
    ...(args.widgets !== undefined ? { widgets: args.widgets } : {}),
  };
  if (args.name !== undefined) {
    paths.push("name");
  }
  if (args.description !== undefined) {
    paths.push("description");
  }
  if (args.widgets !== undefined) {
    paths.push("widgets");
  }
  return {
    dashboard,
    updateMask: { paths },
    ...(args.controls !== undefined ? { controls: args.controls } : {}),
  };
};

/**
 * Predict the GetDashboard response after an update, mirroring the server's apply rules. Powers
 * the optimistic cache write in `useUpdateDashboard`; the input response is not mutated.
 */
export const applyUpdateToGetDashboardResponse = (
  previous: GetDashboardResponse,
  request: UpdateDashboardRequestInit,
): GetDashboardResponse => {
  const next = clone(GetDashboardResponseSchema, previous);
  const paths = request.updateMask?.paths ?? [];
  if (next.dashboard != null) {
    if (paths.includes("name")) {
      next.dashboard.name = request.dashboard?.name ?? "";
    }
    if (paths.includes("description")) {
      // Mirrors the server: masked-and-unset clears the description, and a blank one normalizes to
      // unset so the optimistic state can't show an empty description the server wouldn't store.
      const description = request.dashboard?.description;
      next.dashboard.description =
        description != null && description.trim() !== "" ? description : undefined;
    }
    if (paths.includes("widgets")) {
      next.dashboard.widgets = (request.dashboard?.widgets ?? []).map((widget) =>
        create(DashboardWidgetSchema, widget),
      );
    }
  }
  if (request.controls != null) {
    next.controls = create(DashboardControlsSchema, request.controls);
  }
  return next;
};

/**
 * Apply new grid positions (by widget id) to a dashboard's widgets for persistence via
 * `buildUpdateDashboardRequest`. Unmentioned ids keep their position; unknown ids are ignored.
 */
export const applyWidgetPositions = (
  widgets: readonly DashboardWidget[],
  positionsById: Readonly<Record<string, GridPositionInit>>,
): DashboardWidgetInit[] =>
  widgets.map((widget) => ({
    id: widget.id,
    position: positionsById[widget.id] ?? widget.position,
    definition: widget.definition,
  }));

/** Project settled placements back onto the widgets: the hinge between the layout engine, which
 * speaks placements, and the persisted widget list. */
export const applyPlacements = (
  widgets: readonly DashboardWidget[],
  placements: readonly WidgetPlacement[],
): DashboardWidgetInit[] => applyWidgetPositions(widgets, placementsToPositionsMap(placements));

/** Run the board through a layout pass — the cascade every mutation that changes a footprint needs. */
const resettleWidgets = (
  widgets: readonly DashboardWidget[],
  settle: (placements: WidgetPlacement[]) => WidgetPlacement[],
): DashboardWidgetInit[] => applyPlacements(widgets, settle(widgets.map(widgetToPlacement)));

/** Drop a widget, floating the survivors up so the delete doesn't leave a hole. */
export const removeWidget = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
): DashboardWidgetInit[] =>
  resettleWidgets(
    widgets.filter((widget) => widget.id !== widgetId),
    (placements) => compactVertical(placements),
  );

/**
 * Swap one widget's definition (an edit), keeping its id and position. Other widgets pass through
 * unchanged; an unknown id is a no-op list copy.
 */
export const replaceWidgetDefinition = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
  definition: DashboardWidgetDefinitionInit,
): DashboardWidgetInit[] =>
  widgets.map((widget) =>
    widget.id === widgetId ? { id: widget.id, position: widget.position, definition } : widget,
  );

/** Swap a data widget's definition, growing the tile to the new visualization's default footprint
 * (a 3×1 statistic is unreadable as a timeseries) and settling the board around it. Never shrinks. */
export const replaceDataWidgetDefinition = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
  edited: DashboardDataWidgetInit,
): DashboardWidgetInit[] => {
  const definition: DashboardWidgetDefinitionInit = { case: "dataWidget", value: edited };
  const target = widgets.find((widget) => widget.id === widgetId);
  if (target == null) return replaceWidgetDefinition(widgets, widgetId, definition);
  const layout = widgetToLayout(target);
  const footprint = DATA_WIDGET_FOOTPRINTS[dataWidgetFootprintKind(edited)];
  const grown = {
    ...layout,
    w: Math.min(GRID_COLS, Math.max(layout.w, footprint.w)),
    h: Math.max(layout.h, footprint.h),
  };
  if (layoutsEqual(grown, layout)) return replaceWidgetDefinition(widgets, widgetId, definition);
  // A wider visualization cannot inherit a right-edge x coordinate. Start it below its old slot;
  // vertical compaction finds the first open row without displacing the widgets to its left.
  const targetLayout =
    grown.x + grown.w > GRID_COLS ? { ...grown, x: 0, y: layout.y + layout.h } : grown;
  const settled = resettleWidgets(widgets, (placements) =>
    applyLayout(placements, widgetId, targetLayout),
  );
  return settled.map((widget) =>
    widget.id === widgetId ? { id: widget.id, position: widget.position, definition } : widget,
  );
};

/** A section as a row range `[startY, endY)`; the last one is open-ended. */
interface SectionBand {
  startY: number;
  endY: number;
}

/** The band of the section containing row `y`. A section title heads the section it starts. */
const sectionBandAtRow = (widgets: readonly DashboardWidget[], y: number): SectionBand => {
  const bands = sectionBands(widgets);
  return bands.find((band) => y >= band.startY && y < band.endY) ?? bands[0];
};

/** Topmost row of the last section: the row the lowest widget starts on. */
const lastOccupiedRow = (widgets: readonly DashboardWidget[]): number =>
  widgets.reduce((acc, widget) => Math.max(acc, widgetToLayout(widget).y), 0);

/** Every section as a row band, in order. A board with no section titles is one open-ended band. */
const sectionBands = (widgets: readonly DashboardWidget[]): SectionBand[] => {
  const titleRows = widgets
    .filter((candidate) => candidate.definition.case === "sectionTitle")
    .map((title) => widgetToLayout(title).y)
    .sort((a, b) => a - b);
  const starts = titleRows[0] === 0 ? titleRows : [0, ...titleRows];
  return starts.map((startY, index) => ({ startY, endY: starts[index + 1] ?? Infinity }));
};

/** Rows of the visible range a section covers. */
const visibleRowOverlap = (band: SectionBand, visible: { first: number; last: number }): number =>
  Math.max(0, Math.min(band.endY, visible.last + 1) - Math.max(band.startY, visible.first));

/** Sections covering at least a quarter of the visible rows, so one peeking in by a sliver isn't
 * read as the section you are looking at; the largest share alone when none clears that bar. */
const sectionsOnScreen = (
  widgets: readonly DashboardWidget[],
  visible: { first: number; last: number },
): SectionBand[] => {
  const bands = sectionBands(widgets);
  const visibleRows = Math.max(1, visible.last - visible.first + 1);
  const overlaps = bands.map((band) => ({ band, rows: visibleRowOverlap(band, visible) }));
  const meaningful = overlaps.filter(({ rows }) => rows >= visibleRows / 4);
  if (meaningful.length > 0) return meaningful.map(({ band }) => band);
  const best = overlaps.reduce((acc, next) => (next.rows > acc.rows ? next : acc));
  return [best.band];
};

/** A row inside the section that dominates the viewport — where a new widget belongs. */
export const dominantSectionRow = (
  widgets: readonly DashboardWidget[],
  visible: { first: number; last: number },
): number => {
  const bands = sectionsOnScreen(widgets, visible);
  return bands.reduce((acc, band) =>
    visibleRowOverlap(band, visible) > visibleRowOverlap(acc, visible) ? band : acc,
  ).startY;
};

/** A row inside the last section on screen — where a new section title belongs. */
export const lastSectionOnScreenRow = (
  widgets: readonly DashboardWidget[],
  visible: { first: number; last: number },
): number => sectionsOnScreen(widgets, visible).at(-1)!.startY;

/** Row just below the lowest widget in the band, i.e. where the section ends today. */
const sectionEndRow = (widgets: readonly DashboardWidget[], band: SectionBand): number =>
  widgets.reduce((acc, widget) => {
    const { y, h } = widgetToLayout(widget);
    return y >= band.startY && y < band.endY ? Math.max(acc, y + h) : acc;
  }, band.startY);

/** Row to start scanning from to land at a section's end: a widget-height above it, so a half-empty
 * last row gets filled before the section grows. A full-width widget never fits there. */
const sectionTailRow = (
  widgets: readonly DashboardWidget[],
  band: SectionBand,
  height: number,
): number => Math.max(band.startY, sectionEndRow(widgets, band) - height);

/**
 * Fit a w×h widget into `band`, scanning down from `searchFromY`. A band with no room grows: the
 * widget takes the row the section ends on and everything at or below that row slides down by `h`.
 */
const placeInSection = (
  widgets: readonly DashboardWidget[],
  size: { w: number; h: number },
  band: SectionBand,
  searchFromY: number,
): { position: GridPositionInit; widgets: DashboardWidgetInit[] } => {
  const { w, h } = size;
  const spot = findFreeSpotInBand(widgets.map(widgetToLayout), w, h, searchFromY, band.endY);
  if (spot != null)
    return { position: { ...spot, w, h }, widgets: applyWidgetPositions(widgets, {}) };
  const insertY = sectionEndRow(widgets, band);
  return {
    position: { x: 0, y: insertY, w, h },
    widgets: resettleWidgets(widgets, (placements) =>
      placements.map((placement) =>
        placement.layout.y >= insertY
          ? { ...placement, layout: { ...placement.layout, y: placement.layout.y + h } }
          : placement,
      ),
    ),
  };
};

/**
 * Duplicate one widget: a deep copy with a fresh id and the same footprint, placed in the source's
 * section. A section with no room grows, sliding everything below it down.
 * Returns the full replacement widget list; null for an unknown id or definition-less widget.
 */
export const duplicateWidget = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
): DashboardWidgetInit[] | null => {
  const source = widgets.find((widget) => widget.id === widgetId);
  if (source == null) return null;
  // Deep-clone so editing the copy can't reach back into the cached source message.
  const cloned = clone(DashboardWidgetSchema, source);
  if (cloned.definition.case == null) return null;
  const layout = widgetToLayout(source);
  const band = sectionBandAtRow(widgets, layout.y);
  const placed = placeInSection(widgets, layout, band, band.startY);
  return [
    ...placed.widgets,
    { id: newWidgetId(), position: placed.position, definition: cloned.definition },
  ];
};

// Marks clipboard text as copied dashboard widgets; bump only for an incompatible envelope change.
const WIDGET_CLIPBOARD_VERSION = 1;

/** Widget kinds a dashboard renders, and so the kinds that copy and paste. */
export type PasteableWidgetKind = Exclude<
  NonNullable<DashboardWidget["definition"]["case"]>,
  "notebookCell"
>;

/** A widget read off the clipboard. It carries no position: the receiving dashboard places it. */
export interface ClipboardWidget {
  kind: PasteableWidgetKind;
  definition: DashboardWidget["definition"];
  size: { w: number; h: number };
}

/** Clipboard text for one widget: proto JSON in a marked envelope. x/y belong to the source board,
 * so only the footprint travels (proto JSON omits the zeroed coordinates). */
export const widgetClipboardText = (widget: DashboardWidget): string => {
  const { w, h } = widgetToLayout(widget);
  const copy = create(DashboardWidgetSchema, { position: { w, h }, definition: widget.definition });
  return JSON.stringify(
    {
      chalk_dashboard_widgets: WIDGET_CLIPBOARD_VERSION,
      widgets: [toJson(DashboardWidgetSchema, copy)],
    },
    null,
    2,
  );
};

/** The widgets in clipboard text from `widgetClipboardText`, or null for anything else. Unknown
 * fields are ignored so a copy from a newer frontend still pastes into an older tab. */
export const parseWidgetClipboardText = (text: string): ClipboardWidget[] | null => {
  let widgets: DashboardWidget[];
  try {
    const parsed: unknown = JSON.parse(text);
    if (
      typeof parsed !== "object" ||
      parsed == null ||
      !("chalk_dashboard_widgets" in parsed) ||
      parsed.chalk_dashboard_widgets !== WIDGET_CLIPBOARD_VERSION ||
      !("widgets" in parsed) ||
      !Array.isArray(parsed.widgets)
    ) {
      return null;
    }
    widgets = parsed.widgets.map((json: JsonValue) =>
      fromJson(DashboardWidgetSchema, json, { ignoreUnknownFields: true }),
    );
  } catch {
    return null;
  }
  const pasteable = widgets.flatMap((widget): ClipboardWidget[] => {
    const kind = widget.definition.case;
    if (kind == null || kind === "notebookCell") return [];
    const { w, h } = widgetToLayout(widget);
    return [{ kind, definition: widget.definition, size: { w, h } }];
  });
  return pasteable.length > 0 ? pasteable : null;
};

/**
 * Add a widget at the end of the section holding `atRow` instead of wherever the board first has
 * room, so on a long board it lands where the user is looking. The scan starts a widget-height above
 * the section's end, filling out a half-empty last row before growing the section — a full-width
 * widget never fits there, so a section title always lands on a fresh row.
 */
export const appendWidgetInSection = (
  widgets: readonly DashboardWidget[],
  build: (position: GridPositionInit) => DashboardWidgetInit,
  size: { w: number; h: number },
  atRow: number,
): DashboardWidgetInit[] => {
  const band = sectionBandAtRow(widgets, atRow);
  const placed = placeInSection(widgets, size, band, sectionTailRow(widgets, band, size.h));
  return [...placed.widgets, build(placed.position)];
};

/**
 * Where the "add widget" ghost tile sits: the first fitting space in the last section. The final
 * band is open-ended, so this only ever finds space — it never displaces a real widget the way an
 * insert would.
 */
export const ghostWidgetLayout = (
  widgets: readonly DashboardWidget[],
  size: { w: number; h: number },
): WidgetLayout => {
  const lastBand = sectionBandAtRow(widgets, lastOccupiedRow(widgets));
  const endRow = sectionEndRow(widgets, lastBand);
  const spot = findFreeSpotInBand(
    widgets.map(widgetToLayout),
    size.w,
    size.h,
    lastBand.startY,
    Infinity,
  );
  return { ...(spot ?? { x: 0, y: endRow }), ...size };
};

/** Convenience: the widgets of a (possibly absent) dashboard, never undefined. */
export const dashboardWidgets = (dashboard: Dashboard | undefined): DashboardWidget[] =>
  dashboard?.widgets ?? [];

/** The `DashboardDataWidget` for a data widget widget, or null for any other widget kind. */
export const dataWidgetOf = (widget: DashboardWidget): DashboardDataWidget | null =>
  widget.definition.case === "dataWidget" ? widget.definition.value : null;

/** Widget kinds with an edit destination or an in-dashboard editor. */
export const dashboardWidgetCanEdit = (widget: DashboardWidget): boolean =>
  dataWidgetOf(widget) != null ||
  widget.definition.case === "markdown" ||
  widget.definition.case === "sectionTitle" ||
  widget.definition.case === "monitorWidget" ||
  widget.definition.case === "connectionHealth" ||
  widget.definition.case === "incidentWidget";

/**
 * A metric data widget is the alert/codegen-free subset of `MetricConfig`. These converters are the
 * round-trip across that boundary — the editor and renderer both speak `MetricConfig`.
 */

/**
 * From the metric editor's output: keep the chart-shaping fields, drop alert/codegen/id. The
 * timeseries presentation the editor doesn't expose carries over from the existing data widget when
 * editing in place; `plotStyle`, picked in the editor's preview, overrides the carried-over one.
 */
export const metricConfigToDashboardDataWidget = (
  config: MetricConfig,
  opts?: {
    existing?: DashboardDataWidget;
    visualization?: DashboardDataWidgetInit["visualization"];
    plotStyle?: MetricChartPlotStyle;
  },
): DashboardDataWidgetInit => {
  const existing =
    opts?.existing?.visualization.case === "timeseries" ? opts.existing.visualization.value : null;
  return {
    name: config.name,
    dataQuery: {
      case: "metricQuery",
      value: {
        windowPeriod: config.windowPeriod,
        series: config.series,
        formulas: config.formulas,
        displayWindowPeriod: config.displayWindowPeriod,
      },
    },
    visualization: opts?.visualization ?? {
      case: "timeseries",
      value: {
        plotStyle: opts?.plotStyle ?? existing?.plotStyle,
        initiallyHiddenSeries: existing?.initiallyHiddenSeries,
        yAxisLabel: existing?.yAxisLabel,
        hideFalsyInTooltip: existing?.hideFalsyInTooltip,
        yAxisSoftMin: existing?.yAxisSoftMin,
        yAxisSoftMax: existing?.yAxisSoftMax,
      },
    },
  };
};

/** The `DashboardStatisticViz` of a data widget, or null if it isn't a statistic tile. */
/** The table widget's stored column presentation, in the renderer's shape. */
export const tableColumnLayoutOf = (widget: DashboardWidget): TableColumnLayout | undefined => {
  const viz = dataWidgetOf(widget)?.visualization;
  if (viz?.case !== "table") return undefined;
  return {
    columns: viz.value.columns.map((column) => ({
      key: column.key,
      widthPx: column.widthPx,
      visible: column.visible,
    })),
    columnOrder: viz.value.columnOrder,
  };
};

/**
 * Replace one table widget's stored column presentation. Returns null when the widget is not a
 * table — a stale save racing an edit that switched the visualization has nothing to write.
 */
export const applyTableColumnLayout = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
  layout: TableColumnLayout,
): DashboardWidgetInit[] | null => {
  const widget = widgets.find((candidate) => candidate.id === widgetId);
  const dataWidget = widget == null ? null : dataWidgetOf(widget);
  if (dataWidget == null || dataWidget.visualization.case !== "table") return null;
  const next = clone(DashboardDataWidgetSchema, dataWidget);
  next.visualization = {
    case: "table",
    value: create(DashboardTableVizSchema, {
      columns: layout.columns.map((column) => ({
        key: column.key,
        widthPx: column.widthPx,
        visible: column.visible,
      })),
      columnOrder: [...layout.columnOrder],
    }),
  };
  return replaceWidgetDefinition(widgets, widgetId, { case: "dataWidget", value: next });
};

/**
 * Replace one timeseries widget's stored plot style, keeping the rest of its presentation. Returns
 * null when the widget is no longer a timeseries or already has that style, so there is nothing to write.
 */
export const applyTimeseriesPlotStyle = (
  widgets: readonly DashboardWidget[],
  widgetId: string,
  plotStyle: MetricChartPlotStyle,
): DashboardWidgetInit[] | null => {
  const widget = widgets.find((candidate) => candidate.id === widgetId);
  const dataWidget = widget == null ? null : dataWidgetOf(widget);
  if (dataWidget == null || dataWidget.visualization.case !== "timeseries") return null;
  if (dataWidget.visualization.value.plotStyle === plotStyle) return null;
  const next = clone(DashboardDataWidgetSchema, dataWidget);
  if (next.visualization.case !== "timeseries") return null;
  next.visualization.value.plotStyle = plotStyle;
  return replaceWidgetDefinition(widgets, widgetId, { case: "dataWidget", value: next });
};

export const statisticVizOf = (dataWidget: DashboardDataWidget): DashboardStatisticViz | null =>
  dataWidget.visualization.case === "statistic" ? dataWidget.visualization.value : null;

/** For rendering and editing: project a stored metric data widget back into the `MetricConfig` the chart
 * renderer (`SavedMetricChart`/`getChartSnapshot`) expects. A statistic widget sets stat_options so
 * `getChartSnapshot` evaluates the whole range as one bucket and returns the scalar. */
export const dashboardDataWidgetToMetricConfig = (
  dataWidget: DashboardDataWidget,
): MetricConfig | null => {
  if (dataWidget.dataQuery.case !== "metricQuery") return null;
  const mq = dataWidget.dataQuery.value;
  const isStatistic = dataWidget.visualization.case === "statistic";
  return create(MetricConfigSchema, {
    name: dataWidget.name,
    windowPeriod: mq.windowPeriod,
    series: mq.series,
    formulas: mq.formulas,
    displayWindowPeriod: mq.displayWindowPeriod,
    visualizationOptions: isStatistic ? { case: "statOptions", value: {} } : { case: undefined },
  });
};

/**
 * Adapt a metric data widget widget into the renderer's `ChartWithDefinedConfig`, reusing the widget id
 * as the chart id. Null for other widgets; config is always present so `chartHasConfig` holds.
 */
export const dashboardChartWidgetToChart = (
  widget: DashboardWidget,
): ChartWithDefinedConfig | null => {
  const dataWidget = dataWidgetOf(widget);
  if (dataWidget == null) return null;
  const config = dashboardDataWidgetToMetricConfig(dataWidget);
  if (config == null) return null;
  return create(ChartSchema, { id: widget.id, config }) as ChartWithDefinedConfig;
};

/**
 * Default footprint for a new chart-like data widget: a quarter-width widget (`findFreeSpot` picks where it
 * lands). Also the fallback footprint for stored widgets with a missing/zero dimension.
 */
export const CHART_WIDGET_WIDTH = 4;
export const CHART_WIDGET_HEIGHT = 4;

/** A log table needs more room than a chart to be useful, so it defaults to a larger slot. */
export const LOG_TABLE_WIDGET_WIDTH = 8;
export const LOG_TABLE_WIDGET_HEIGHT = 6;

/** A statistic is a single value, so it defaults to a small, one-row-tall tile. */
export const STAT_WIDGET_WIDTH = 3;
export const STAT_WIDGET_HEIGHT = 1;

/** Markdown notes default to a half-width text card. */
export const MARKDOWN_WIDGET_WIDTH = 6;
export const MARKDOWN_WIDGET_HEIGHT = 3;

/** A section title is a full-width one-row strip. */
export const SECTION_TITLE_WIDGET_WIDTH = 12;
export const SECTION_TITLE_WIDGET_HEIGHT = 1;

/** A notebook cell widget defaults to a half-width card — output shapes vary
 * (chart, table, input control), so it starts roomy and gets resized. */
export const NOTEBOOK_CELL_WIDGET_WIDTH = 6;
export const NOTEBOOK_CELL_WIDGET_HEIGHT = 4;

/** Input and section cells are single-row controls/headings: they default to a
 * one-row strip (inputs a quarter-width) and may resize down to one row. */
export const NOTEBOOK_INPUT_CELL_WIDGET_WIDTH = 3;
export const NOTEBOOK_COMPACT_CELL_WIDGET_HEIGHT = 1;
export const NOTEBOOK_COMPACT_CELL_WIDGET_MIN_HEIGHT = 1;

/**
 * A single-value cell is one number and a caption, so it stays legible narrower
 * than the 3-column default minimum — narrow enough to line up six metrics
 * across a 12-column board.
 */
export const NOTEBOOK_SINGLE_VALUE_CELL_WIDGET_MIN_WIDTH = 2;

/**
 * A COUNT BY table shows a short ranked list (three narrow columns), not a stream of rows, so it
 * defaults to a compact slot — a full-height log-table slot would strand a big empty frame under it.
 */
export const LOG_COUNT_TABLE_WIDGET_WIDTH = 6;
export const LOG_COUNT_TABLE_WIDGET_HEIGHT = 4;

/** Default footprint per data-widget kind; the creation site picks the kind and looks up (w, h). */
export const DATA_WIDGET_FOOTPRINTS = {
  statistic: { w: STAT_WIDGET_WIDTH, h: STAT_WIDGET_HEIGHT },
  countByTable: { w: LOG_COUNT_TABLE_WIDGET_WIDTH, h: LOG_COUNT_TABLE_WIDGET_HEIGHT },
  logTable: { w: LOG_TABLE_WIDGET_WIDTH, h: LOG_TABLE_WIDGET_HEIGHT },
  chart: { w: CHART_WIDGET_WIDTH, h: CHART_WIDGET_HEIGHT },
} as const satisfies Record<string, { w: number; h: number }>;

export type DataWidgetFootprintKind = keyof typeof DATA_WIDGET_FOOTPRINTS;

/** Which default footprint a data widget takes: the visualization, and for a table whether it is a
 * short ranked list or a stream of rows. */
export const dataWidgetFootprintKind = (
  dataWidget: DashboardDataWidgetInit,
): DataWidgetFootprintKind => {
  if (dataWidget.visualization?.case === "statistic") return "statistic";
  if (dataWidget.visualization?.case !== "table") return "chart";
  const sourceQuery =
    dataWidget.dataQuery?.case === "sourceQuery" ? dataWidget.dataQuery.value : null;
  // Ranked either way: structured aggregations, or a legacy widget's COUNT BY still in the query.
  const isRanked =
    (sourceQuery?.aggregateOptions?.aggregations?.length ?? 0) > 0 ||
    parseCountByQuery(sourceQuery?.query ?? "").clause != null;
  return isRanked ? "countByTable" : "logTable";
};

/** Smallest footprint a visualization is ever given; a table has two, so this takes the smaller and
 * the picker's warning never promises a resize that does not happen. */
export const VIZ_MINIMUM_FOOTPRINT: Record<DataWidgetVizCase, { w: number; h: number }> = {
  timeseries: DATA_WIDGET_FOOTPRINTS.chart,
  statistic: DATA_WIDGET_FOOTPRINTS.statistic,
  table: DATA_WIDGET_FOOTPRINTS.countByTable,
  // A ring or a mosaic needs area in both directions, which the chart footprint already asks for.
  treeMap: DATA_WIDGET_FOOTPRINTS.chart,
  pie: DATA_WIDGET_FOOTPRINTS.chart,
};

export const MONITOR_WIDGET_FOOTPRINTS = {
  metric: { w: 4, h: 2 },
  logs: { w: 3, h: 2 },
  healthcheck: { w: 5, h: 3 },
  sqlBadRows: { w: 4, h: 2 },
  dataSource: { w: 5, h: 3 },
  featureDrift: { w: 5, h: 3 },
} as const satisfies Record<MonitorKind, { w: number; h: number }>;

export const CONNECTION_HEALTH_WIDGET_FOOTPRINT = { w: 4, h: 5 } as const;

export const defaultConnectionHealthWidget = (): DashboardConnectionHealthWidgetInit => ({
  connectionIds: [...DEFAULT_DASHBOARD_CONNECTION_HEALTH],
});

export const LATEST_DEPLOYMENT_WIDGET_FOOTPRINT = { w: 8, h: 5 } as const;

export type MonitorWidgetFootprintKind = keyof typeof MONITOR_WIDGET_FOOTPRINTS;

/**
 * Projections from a `DashboardWidget` to the layout-only `WidgetPlacement` the grid uses (the reverse is
 * `applyWidgetPositions`). The default-position fallback lives only in `widgetToLayout`.
 */

/** An widget's stored grid position, defaulting to the standard chart slot when absent (e.g. an
 * widget created before drag/resize, or any widget with a missing/zero dimension).
 *
 * A section title is pinned to the full grid width regardless of what is stored. `sectionBands` keys
 * sections off a title's row alone, which is only sound while the title spans every column: at full
 * width `compactVertical` can never float anything past it, so the row band is a real barrier. A
 * narrower title has free columns beside it and floats up into the previous section itself, silently
 * merging the two. Normalizing on read also repairs any title already persisted narrow. */
export const widgetToLayout = (widget: DashboardWidget): WidgetLayout => {
  if (widget.definition.case === "sectionTitle") {
    return {
      x: 0,
      y: widget.position?.y ?? 0,
      w: GRID_COLS,
      h: widget.position?.h || SECTION_TITLE_WIDGET_HEIGHT,
    };
  }
  if (widget.definition.case === "connectionHealth") {
    return {
      x: widget.position?.x ?? 0,
      y: widget.position?.y ?? 0,
      w: widget.position?.w || CONNECTION_HEALTH_WIDGET_FOOTPRINT.w,
      h: widget.position?.h || CONNECTION_HEALTH_WIDGET_FOOTPRINT.h,
    };
  }

  if (widget.definition.case === "latestDeployment") {
    return {
      x: widget.position?.x ?? 0,
      y: widget.position?.y ?? 0,
      w: widget.position?.w || LATEST_DEPLOYMENT_WIDGET_FOOTPRINT.w,
      h: Math.max(
        widget.position?.h || LATEST_DEPLOYMENT_WIDGET_FOOTPRINT.h,
        LATEST_DEPLOYMENT_WIDGET_FOOTPRINT.h,
      ),
    };
  }
  return {
    x: widget.position?.x ?? 0,
    y: widget.position?.y ?? 0,
    w: widget.position?.w || CHART_WIDGET_WIDTH,
    h: widget.position?.h || CHART_WIDGET_HEIGHT,
  };
};

/** Minimum footprint for widget kinds that stay legible below the default chart floor: a statistic
 * is one number plus a caption, a text widget is free-form prose that scrolls, and a monitor widget
 * is a compact card. Everything else keeps DEFAULT_MIN_W/H. */
const placementMinimum = (widget: DashboardWidget): Pick<WidgetPlacement, "minW" | "minH"> => {
  if (widget.definition.case === "sectionTitle") return { minW: GRID_COLS, minH: 1 };
  if (widget.definition.case === "markdown") return { minW: 1, minH: 1 };
  if (widget.definition.case === "monitorWidget") {
    return { minW: 2, minH: 2 };
  }
  if (widget.definition.case === "connectionHealth") return { minW: 3, minH: 2 };
  if (widget.definition.case === "incidentWidget") return { minW: 3, minH: 3 };
  if (widget.definition.case === "latestDeployment") return { minW: 4, minH: 5 };
  if (dataWidgetOf(widget)?.visualization.case === "statistic") return { minW: 2, minH: 1 };
  return {};
};

/** An widget as the layout-only `WidgetPlacement` the grid renders: id + position, definition dropped. */
export const widgetToPlacement = (widget: DashboardWidget): WidgetPlacement => ({
  id: widget.id,
  layout: widgetToLayout(widget),
  ...placementMinimum(widget),
});

/** Renderable widgets as grid placements, ordered top-to-bottom then left-to-right for stable DOM
 * order. Widgets with an unset definition (malformed / a not-yet-supported kind) are skipped. */
export const widgetsToPlacements = (widgets: readonly DashboardWidget[]): WidgetPlacement[] =>
  widgets
    .filter((widget) => widget.definition.case != null)
    .map(widgetToPlacement)
    .sort((a, b) => a.layout.y - b.layout.y || a.layout.x - b.layout.x);

/** Invert a placement list into the id→position map `applyWidgetPositions` consumes. */
export const placementsToPositionsMap = (
  placements: readonly WidgetPlacement[],
): Record<string, WidgetLayout> => Object.fromEntries(placements.map((w) => [w.id, w.layout]));

/**
 * The title a widget shows when its own name is blank — the aggregation as a phrase, e.g. "Count
 * unique of Pod Name by Service". Derived at render, not frozen into `name` on save, so the title
 * follows the configuration while a name the user typed stays theirs.
 *
 * `facets` supplies only display names, so a caller without the catalog yet still gets raw paths.
 */
export const dataWidgetDefaultName = (
  state: AggregationBuilderState,
  isTable: boolean,
  source: WidgetSourceSpec,
  facets: readonly AggregationFacetOption[],
): string => {
  if (!isAggregationView(state)) {
    return isTable ? source.listTableName : source.listChartName;
  }
  const measures = state.measures.map((measure) => measureLabel(measure, facets)).join(", ");
  const by = state.facets
    .map((path) => facets.find((facet) => facet.path === path)?.name ?? path)
    .join(", ");
  const phrase = by === "" ? measures : `${measures} by ${by}`;
  return phrase === AGGREGATION_FN_LABEL.count ? `${source.label} Count` : phrase;
};
